import { Inject } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { Bezier } from './bezier';
import { Point } from './point';
import { AddPointSignaturePadReturnValue } from './signature-pad-interface';
import { SignaturePadOptions } from './signature-pad-options';

function isMouseEvent(event: MouseEvent | TouchEvent): event is MouseEvent {
    return !!(<MouseEvent>event).clientX;
}

function calculateCurveControlPoints(s1: Point, s2: Point, s3: Point): { c1: Point; c2: Point } {
    const dx1 = s1.x - s2.x;
    const dy1 = s1.y - s2.y;
    const dx2 = s2.x - s3.x;
    const dy2 = s2.y - s3.y;
    const halfDivider = 2.0;

    const m1 = { x: (s1.x + s2.x) / halfDivider, y: (s1.y + s2.y) / halfDivider };
    const m2 = { x: (s2.x + s3.x) / halfDivider, y: (s2.y + s3.y) / halfDivider };

    const l1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
    const l2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

    const dxm = m1.x - m2.x;
    const dym = m1.y - m2.y;

    const k = l2 / (l1 + l2);
    const cm = { x: m2.x + dxm * k, y: m2.y + dym * k };

    const tx = s2.x - cm.x;
    const ty = s2.y - cm.y;

    return {
        c1: new Point(m1.x + tx, m1.y + ty),
        c2: new Point(m2.x + tx, m2.y + ty),
    };
}

interface DataPoint {
    x: number;
    y: number;
    time: number;
    color: string;
}

export type DataPointArray = DataPoint[][];

const minimumPointsAmount = 2;

export class SignaturePad {
    private data: DataPointArray = [];
    private points: Point[];
    private lastVelocity: number;
    private lastWidth: number;
    public isEmpty: boolean;
    private mouseButtonDown: boolean;
    private readonly ctx: CanvasRenderingContext2D;
    private readonly canvas: HTMLCanvasElement;
    private readonly velocityFilterWeight: number;
    private readonly minWidth: number;
    private readonly maxWidth: number;
    private readonly dotSize: number;
    private readonly penColor: string;
    private readonly backgroundColor: string;
    private readonly onEnd: ((event?: MouseEvent | TouchEvent) => void) | undefined;
    private readonly onBegin: ((event?: MouseEvent | TouchEvent) => void) | undefined;
    private readonly throttle: number;
    private readonly strokeMoveUpdate: (event: MouseEvent | TouchEvent) => void;
    public dataUrl = '';

    private readonly handleMouseDown: (event: MouseEvent) => void;
    private readonly handleMouseMove: (event: MouseEvent) => void;
    private readonly handleMouseUp: (event: MouseEvent) => void;
    private readonly handleMouseLeave: (event: MouseEvent) => void;
    private readonly handleTouchStart: (event: TouchEvent) => void;
    private readonly handleTouchMove: (event: TouchEvent) => void;
    private readonly handleTouchEnd: (event: TouchEvent) => void;

    private outputCtx: CanvasRenderingContext2D;
    private outputCanvas: HTMLCanvasElement;
    private readonly outputImageFormat: string;
    private readonly outputImageQuality: number;
    private readonly outputImageMaxSize: number;
    private dataUrlSubject = new BehaviorSubject<string>('');
    public dataUrl$ = this.dataUrlSubject.asObservable();

    private dataPointsSubject = new BehaviorSubject<DataPointArray>([]);
    public dataPoints$ = this.dataPointsSubject.asObservable();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        canvas: HTMLCanvasElement,
        options?: SignaturePadOptions,
    ) {
        const opts = options || {};

        const defaultVelocityFilterWeight = 0.7;
        const defaultMinWidth = 0.5;
        const defaultMaxWidth = 2.5;
        const defaultThrottle = 16;

        this.velocityFilterWeight = opts.velocityFilterWeight || defaultVelocityFilterWeight;
        this.minWidth = opts.minWidth || defaultMinWidth;
        this.maxWidth = opts.maxWidth || defaultMaxWidth;
        this.throttle = opts.throttle ? opts.throttle : defaultThrottle; // in milliseconds

        if (this.throttle) {
            this.strokeMoveUpdate = this.throttleFunction.bind(this);
        } else {
            this.strokeMoveUpdate = this.strokeUpdate.bind(this);
        }

        const defaultSizeDivider = 2;
        this.dotSize = opts.dotSize || (this.minWidth + this.maxWidth) / defaultSizeDivider;

        this.penColor = opts.penColor || 'black';
        this.backgroundColor = opts.backgroundColor || 'rgba(0,0,0,0)';
        this.onBegin = opts.onBegin;
        this.onEnd = opts.onEnd;

        // We need add these inline, so they are available to unbind while still having
        // access to 'this' we could use _.bind, but it's not worth adding a dependency.
        this.handleMouseDown = (event: MouseEvent): void => {
            if (event.button === 0) {
                this.mouseButtonDown = true;
                this.strokeBegin(event);
            }
        };

        this.handleMouseMove = (event: MouseEvent): void => {
            if (this.mouseButtonDown) {
                this.strokeMoveUpdate(event);
            }
        };

        this.handleMouseUp = (event: MouseEvent): void => {
            if (event.button === 0 && this.mouseButtonDown) {
                this.mouseButtonDown = false;
                this.strokeEnd(event);
            }
        };

        this.handleMouseLeave = (event: MouseEvent): void => {
            if (event.button === 0 && this.mouseButtonDown) {
                this.mouseButtonDown = false;
                this.strokeEnd(event);
            }
        };

        this.handleTouchStart = (event: TouchEvent): void => {
            if (event.targetTouches.length === 1) {
                this.strokeBegin(event);
            }
        };

        this.handleTouchMove = (event: TouchEvent): void => {
            // Prevent scrolling.
            event.preventDefault();

            this.strokeMoveUpdate(event);
        };

        this.handleTouchEnd = (event: TouchEvent): void => {
            const wasCanvasTouched = event.target === this.canvas;
            if (wasCanvasTouched) {
                event.preventDefault();
                this.strokeEnd(event);
            }
        };

        this.canvas = canvas;
        let context = canvas.getContext('2d');
        if (!context) {
            throw new Error(`Could not get 2d context for signature canvas!`);
        }
        this.ctx = context;

        // create additional canvas to compose signature on background and do resize
        const resizeFactor = opts.resizeFactor ? opts.resizeFactor : 1;
        const newWidth = this.canvas.width * resizeFactor;
        const newHeight = this.canvas.height * resizeFactor;
        this.outputCanvas = document.createElement('canvas');
        this.outputCanvas.setAttribute('width', newWidth.toString());
        this.outputCanvas.setAttribute('height', newHeight.toString());
        context = this.outputCanvas.getContext('2d');
        if (!context) {
            throw new Error(`Could not get 2d context for signature output canvas!`);
        }

        const defaultOutputImageQuality = 0.9;

        this.outputCtx = context;
        this.outputImageFormat = opts.outputImageType ? opts.outputImageType : 'image/png';
        this.outputImageQuality = opts.outputImageQuality ? opts.outputImageQuality : defaultOutputImageQuality;
        this.outputImageMaxSize = opts.outputImageMaxSize ? opts.outputImageMaxSize : -1;

        this.clear();
        // Enable mouse and touch event handlers
        this.on();
    }

    public clear(): void {
        const ctx = this.ctx;
        const canvas = this.canvas;

        ctx.fillStyle = 'rgba(0, 0, 0, 0)';
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        this.data.length = 0;
        this.isEmpty = true;
        this.reset();
        this.dataUrl = this.toDataURL();
        this.dataUrlSubject.next(this.dataUrl);
        this.dataPointsSubject.next(_.cloneDeep(this.data));
    }

    /**
     * Get the signature composited over the background color. If 'removeHeader' is set to 'true' (default value),
     * then the data url header 'data:image/png;base64,' is being removed before returning the base64 encoded data.
     * This is expected if the signature data is consumed by DSI in the end, for example for the service report or
     * a form
     *
     * @param removeHeader boolean - if true, the header 'data:image/png;base64,' is removed from the base64 encoded data
     * @returns string - base64 encoded data url of the signature
     */
    private toDataURL(removeHeader = true): string {
        this.outputCtx.fillStyle = this.backgroundColor;
        this.outputCtx.fillRect(0, 0, this.outputCanvas.width, this.outputCanvas.height);
        this.outputCtx.drawImage(this.canvas, 0, 0, this.outputCanvas.width, this.outputCanvas.height);

        let adaptedImageQuality = this.outputImageQuality;

        let dataUrl = this.outputCanvas.toDataURL(this.outputImageFormat, adaptedImageQuality);

        // remove header like 'data:image/png;base64,' header if requested
        if (removeHeader) {
            dataUrl = dataUrl.substring(dataUrl.search(',') + 1);
        }

        // if a max size is given for the signature, incrementally increase the compression until the size limit is hit
        if (this.outputImageMaxSize > 0 && this.outputImageFormat === 'image/jpeg') {
            this.log.debug(
                `SignaturePad: toDataURLOutput: Checking if signature needs to be compressed to be smaller than ${this.outputImageMaxSize} bytes'`,
            );

            const imageQualityLowerLimit = 0.01;
            const imageQualityPivot = 0.1;
            const factor100 = 100;
            const fastDecrement = 10;
            const slowDecrement = 1;
            while (dataUrl.length > this.outputImageMaxSize && adaptedImageQuality >= imageQualityLowerLimit) {
                const decrement = adaptedImageQuality > imageQualityPivot ? fastDecrement : slowDecrement;
                adaptedImageQuality = (adaptedImageQuality * factor100 - decrement) / factor100; // use multiplication/division to avoid floating point errors
                this.log.debug(`    Decreasing quality factor to ${adaptedImageQuality}`);
                dataUrl = this.outputCanvas.toDataURL(this.outputImageFormat, adaptedImageQuality);

                // remove header like 'data:image/png;base64,' header if requested
                if (removeHeader) {
                    dataUrl = dataUrl.substring(dataUrl.search(',') + 1);
                }
            }

            if (dataUrl.length > this.outputImageMaxSize) {
                this.log.error(
                    `    Unable to compress signature to enough to fit into ${this.outputImageMaxSize} bytes!`,
                );
            }
        }

        this.log.debug(
            `Signature data length of type '${this.outputImageFormat}' (${this.outputCanvas.width}x${this.outputCanvas.height}) with quality set to ${adaptedImageQuality} results in signature of ${dataUrl.length} characters as base64 encoded string`,
        );

        return dataUrl;
    }

    public on(): void {
        this.handleMouseEvents();
        this.handleTouchEvents();
    }

    public off(): void {
        this.canvas.removeEventListener('mousedown', this.handleMouseDown);
        this.canvas.removeEventListener('mousemove', this.handleMouseMove);
        this.canvas.removeEventListener('mouseleave', this.handleMouseLeave);
        document.removeEventListener('mouseup', this.handleMouseUp);

        this.canvas.removeEventListener('touchstart', this.handleTouchStart);
        this.canvas.removeEventListener('touchmove', this.handleTouchMove);
        this.canvas.removeEventListener('touchend', this.handleTouchEnd);
    }

    private strokeBegin(event: MouseEvent | TouchEvent): void {
        this.data.push([]);
        this.reset();
        this.strokeUpdate(event);

        if (typeof this.onBegin === 'function') {
            this.onBegin(event);
        }
    }

    private strokeUpdate(event: MouseEvent | TouchEvent): void {
        const x = isMouseEvent(event) ? event.clientX : event.changedTouches[0].clientX;
        const y = isMouseEvent(event) ? event.clientY : event.changedTouches[0].clientY;

        const newPoint = this.createPoint(x, y);
        const { curve, widths } = this.addPoint(newPoint, this.points);

        if (curve && widths) {
            this.drawCurve(curve, widths.start, widths.end);
        }

        // this might be happening if the data is reset during a stroke
        if (this.data.length === 0) {
            this.data.push([]);
        }

        this.data[this.data.length - 1].push({
            x: newPoint.x,
            y: newPoint.y,
            time: newPoint.time,
            color: this.penColor,
        });
    }

    private throttleFunction(event: MouseEvent | TouchEvent): void {
        let timeout = 0;
        let previous = 0;

        const throttle = this.throttle;

        const later = (): void => {
            previous = Date.now();
            timeout = 0;
            this.strokeUpdate(event);
        };

        const now = Date.now();
        if (!previous) {
            previous = now;
        }
        const remaining = throttle - (now - previous);
        if (remaining <= 0 || remaining >= throttle) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = 0;
            }
            previous = now;
            this.strokeUpdate(event);
        } else if (!timeout) {
            timeout = window.setTimeout(later, remaining);
        }
    }

    private strokeEnd(event: MouseEvent | TouchEvent): void {
        const canDrawCurve = this.points.length > minimumPointsAmount;
        const startingPoint = this.points[0]; // Point instance

        if (!canDrawCurve && startingPoint) {
            this.drawDot(startingPoint);
        }

        if (startingPoint) {
            const lastPointGroup = this.data[this.data.length - 1];
            const lastPointObject = lastPointGroup[lastPointGroup.length - 1]; // plain object
            const lastPoint = new Point(lastPointObject.x, lastPointObject.y, lastPointObject.time);

            // When drawing a dot, there's only one point in a group, so without this check
            // such group would end up with exactly the same 2 points.
            if (!startingPoint.equals(lastPoint)) {
                lastPointGroup.push({
                    x: startingPoint.x,
                    y: startingPoint.y,
                    time: startingPoint.time,
                    color: this.penColor,
                });
            }
        }

        this.dataUrl = this.toDataURL();
        this.dataUrlSubject.next(this.dataUrl);
        this.dataPointsSubject.next(_.cloneDeep(this.data));

        if (typeof this.onEnd === 'function') {
            this.onEnd(event);
        }
    }

    private handleMouseEvents(): void {
        this.mouseButtonDown = false;

        this.canvas.addEventListener('mousedown', this.handleMouseDown);
        this.canvas.addEventListener('mousemove', this.handleMouseMove);
        this.canvas.addEventListener('mouseleave', this.handleMouseLeave);
        document.addEventListener('mouseup', this.handleMouseUp);
    }

    private handleTouchEvents(): void {
        // Pass touch events to canvas element on mobile IE11 and Edge.
        this.canvas.style.touchAction = 'none';

        this.canvas.addEventListener('touchstart', this.handleTouchStart);
        this.canvas.addEventListener('touchmove', this.handleTouchMove);
        this.canvas.addEventListener('touchend', this.handleTouchEnd);
    }

    private reset(): void {
        this.points = [];
        this.lastVelocity = 0;
        const halfDivider = 2;
        this.lastWidth = (this.minWidth + this.maxWidth) / halfDivider;
        this.ctx.fillStyle = this.penColor;
    }

    private createPoint(x: number, y: number, time?: number): Point {
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        return new Point((x - rect.left) * scaleX, (y - rect.top) * scaleY, time || new Date().getTime());
    }

    private addPoint(newPoint: Point, points: Point[]): AddPointSignaturePadReturnValue {
        let tmp;
        points.push(newPoint);

        if (points.length > minimumPointsAmount) {
            // To reduce the initial lag make it work with 3 points
            // by copying the first point to the beginning.
            if (points.length === minimumPointsAmount + 1) {
                points.unshift(points[0]);
            }

            const firstPointIndex = 0;
            const secondPointIndex = 1;
            const thirdPointIndex = 2;
            const fourthPointIndex = 3;

            tmp = calculateCurveControlPoints(
                points[firstPointIndex],
                points[secondPointIndex],
                points[thirdPointIndex],
            );
            const c2 = tmp.c2;
            tmp = calculateCurveControlPoints(
                points[secondPointIndex],
                points[thirdPointIndex],
                points[fourthPointIndex],
            );
            const c3 = tmp.c1;
            const curve = new Bezier(points[1], c2, c3, points[thirdPointIndex]);
            const widths = this.calculateCurveWidths(curve);

            // Remove the first element from the list,
            // so that we always have no more than 4 points in points array.
            points.shift();

            return { curve, widths };
        }

        return {};
    }

    private calculateCurveWidths(curve: Bezier): { start: number; end: number } {
        const startPoint = curve.startPoint;
        const endPoint = curve.endPoint;
        const start = 0;
        const end = 0;
        const widths = { start, end };

        const velocity =
            this.velocityFilterWeight * endPoint.velocityFrom(startPoint) +
            (1 - this.velocityFilterWeight) * this.lastVelocity;

        const newWidth = this.strokeWidth(velocity);

        widths.start = this.lastWidth;
        widths.end = newWidth;

        this.lastVelocity = velocity;
        this.lastWidth = newWidth;

        return widths;
    }

    private strokeWidth(velocity: number): number {
        return Math.max(this.maxWidth / (velocity + 1), this.minWidth);
    }

    private drawPoint(x: number, y: number, size: number): void {
        const ctx = this.ctx;

        const doubleMultiplier = 2;
        ctx.moveTo(x, y);
        ctx.arc(x, y, size, 0, doubleMultiplier * Math.PI, false);
        this.isEmpty = false;
    }

    private drawCurve(curve: Bezier, startWidth: number, endWidth: number): void {
        const ctx = this.ctx;
        const widthDelta = endWidth - startWidth;
        const drawSteps = Math.floor(curve.length());

        ctx.beginPath();

        for (let i = 0; i < drawSteps; i += 1) {
            // Calculate the Bezier (x, y) coordinate for this step.
            const t = i / drawSteps;
            const tt = t * t;
            const ttt = tt * t;
            const u = 1 - t;
            const uu = u * u;
            const uuu = uu * u;
            const multiplier = 3;

            let x = uuu * curve.startPoint.x;
            x += multiplier * uu * t * curve.control1.x;
            x += multiplier * u * tt * curve.control2.x;
            x += ttt * curve.endPoint.x;

            let y = uuu * curve.startPoint.y;
            y += multiplier * uu * t * curve.control1.y;
            y += multiplier * u * tt * curve.control2.y;
            y += ttt * curve.endPoint.y;

            const width = startWidth + ttt * widthDelta;
            this.drawPoint(x, y, width);
        }

        ctx.closePath();
        ctx.fill();
    }

    private drawDot(startingPoint: Point): void {
        const ctx = this.ctx;
        const dotSize = this.dotSize;

        ctx.beginPath();
        this.drawPoint(startingPoint.x, startingPoint.y, dotSize);
        ctx.closePath();
        ctx.fill();
    }

    private fromDataInternal(
        pointGroups: DataPointArray,
        drawCurve: (curve: Bezier, widths: { start: number; end: number }, color: string) => void,
        drawDot: (point: unknown) => void,
    ): void {
        for (const group of pointGroups) {
            if (group.length > 1) {
                for (let j = 0; j < group.length; j += 1) {
                    const rawPoint = group[j];
                    const newPoint = new Point(rawPoint.x, rawPoint.y, rawPoint.time);
                    const color = rawPoint.color;

                    if (j === 0) {
                        // First point in a group. Nothing to draw yet.
                        this.reset();
                        this.addPoint(newPoint, this.points);
                    } else if (j !== group.length - 1) {
                        // Middle point in a group.
                        const { curve, widths } = this.addPoint(newPoint, this.points);
                        if (curve && widths) {
                            drawCurve(curve, widths, color);
                        }
                    } else {
                        // Last point in a group. Do nothing.
                    }
                }
            } else {
                this.reset();
                const rawArrayPoint = group[0];
                const rawPoint = new Point(rawArrayPoint.x, rawArrayPoint.y, rawArrayPoint.time);
                drawDot(rawPoint);
            }
        }
    }

    private toSVG(): unknown {
        const pointGroups = this.data;
        const canvas = this.canvas;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const minX = 0;
        const minY = 0;
        const maxX = canvas.width / ratio;
        const maxY = canvas.height / ratio;
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

        svg.setAttributeNS('', 'width', canvas.width.toString());
        svg.setAttributeNS('', 'height', canvas.height.toString());

        this.fromDataInternal(
            pointGroups,
            (curve: Bezier, widths: { start: number; end: number }, color: string) => {
                const path = document.createElement('path');
                const toFixed = 3;
                const widthEndMultiplier = 2.25;
                // Need to check curve for NaN values, these pop up when drawing
                // lines on the canvas that are not continuous. E.g. Sharp corners
                // or stopping mid-stroke and then continuing without lifting mouse.
                if (
                    !isNaN(curve.control1.x) &&
                    !isNaN(curve.control1.y) &&
                    !isNaN(curve.control2.x) &&
                    !isNaN(curve.control2.y)
                ) {
                    const attr =
                        `M ${curve.startPoint.x.toFixed(toFixed)},${curve.startPoint.y.toFixed(toFixed)} ` +
                        `C ${curve.control1.x.toFixed(toFixed)},${curve.control1.y.toFixed(toFixed)} ` +
                        `${curve.control2.x.toFixed(toFixed)},${curve.control2.y.toFixed(toFixed)} ` +
                        `${curve.endPoint.x.toFixed(toFixed)},${curve.endPoint.y.toFixed(toFixed)}`;

                    path.setAttribute('d', attr);
                    path.setAttribute('stroke-width', (widths.end * widthEndMultiplier).toFixed(toFixed));
                    path.setAttribute('stroke', color);
                    path.setAttribute('fill', 'none');
                    path.setAttribute('stroke-linecap', 'round');

                    svg.appendChild(path);
                }
            },
            (rawPoint: DataPoint) => {
                const circle = document.createElement('circle');
                const dotSize = this.dotSize;
                circle.setAttribute('r', dotSize.toString());
                circle.setAttribute('cx', rawPoint.x.toString());
                circle.setAttribute('cy', rawPoint.y.toString());
                circle.setAttribute('fill', rawPoint.color);

                svg.appendChild(circle);
            },
        );

        const prefix = 'data:image/svg+xml;base64,';
        const header =
            '<svg' +
            ' xmlns="http://www.w3.org/2000/svg"' +
            ' xmlns:xlink="http://www.w3.org/1999/xlink"' +
            ` viewBox="${minX} ${minY} ${maxX} ${maxY}"` +
            ` width="${maxX}"` +
            ` height="${maxY}"` +
            '>';
        let body = svg.innerHTML;

        // IE hack for missing innerHTML property on SVGElement
        if (body === undefined) {
            const dummy = document.createElement('dummy');
            const nodes = svg.childNodes;
            dummy.innerHTML = '';

            nodes.forEach((node) => {
                dummy.appendChild(node.cloneNode(true));
            });

            body = dummy.innerHTML;
        }

        const footer = '</svg>';
        const data = header + body + footer;

        return prefix + btoa(data);
    }

    public restoreFromDataPoints(newData: DataPointArray): void {
        this.clear();

        newData.forEach((stroke) => this.data.push(_.cloneDeep(stroke)));

        this.fromDataInternal(
            this.data,
            (curve: Bezier, widths: { start: number; end: number }) => this.drawCurve(curve, widths.start, widths.end),
            (rawPoint: Point) => this.drawDot(rawPoint),
        );
        this.dataUrl = this.toDataURL();
        this.dataUrlSubject.next(this.dataUrl);
        this.dataPointsSubject.next(this.data);
    }
}
